import {
  Box,
  Button,
  Checkbox,
  Grid,
  OutlinedInput,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import useInputObject from "../../../hooks/useInputObject";
import DateInput from "../../../components/DateInput";
import { DATE_MODE, DATE_STR_START, MONTH_STR_END } from "../../../components/DateInput";
import { NormalTable } from "../../../components/TableCustomize";
import { getPayableDailyConfirmation } from "../../../http/api/BalanceAPI";
import { getHyphenedDate } from "../../../utils/utils";
import { CompanyTitledPage } from "../../TitledPage";
import { accountPaymentColumn, accountPaymentMonthlyColumn, totalPaymentColumn } from "./enhance";

const AccountSearchArea = ({
  onSearchCondition,
  searchConditionInput,
  changeSearchConditionInput,
}) => {
  const onFocusInput = (event) => {
    event.target.select();
  };

  const onClickSearch = (event) => {
    onSearchCondition();
  };

  const handleChangeSearchCondition = (e) => {
    changeSearchConditionInput(e);
  };

  return (
    <Box border="1px solid blue" borderRadius={1} padding={2}>
      検索
      <Box margin={2}>
        <Grid container alignItems="end" spacing={2}>
          <Grid item xs>
            <Stack
              direction="row"
              alignItems="center"
              flexWrap="wrap"
              justifyContent={{ xl: "space-between", lg: "left" }}
            >
              <Stack
                direction="row"
                alignItems="center"
                flexWrap="wrap"
                justifyContent={{ sm: "space-between", md: "left" }}
                width={{ md: "auto", sm: 380 }}
                marginBottom={{ xl: 0, xs: 2 }}
              >
                <Typography>荷主(No/氏名):&nbsp;</Typography>
                <OutlinedInput
                  onFocus={onFocusInput}
                  onChange={({ target: { value } }) =>
                    handleChangeSearchCondition({ keyWord: value })
                  }
                  value={searchConditionInput.keyWord}
                  sx={{ width: 250 }}
                />
              </Stack>
              <Stack
                direction="row"
                alignItems="center"
                flexWrap="wrap"
                justifyContent={{ sm: "space-between", md: "left" }}
                width={{ md: "auto", sm: 380 }}
                marginBottom={{ xl: 0, xs: 2 }}
              >
                <Typography>送り状番号:&nbsp;</Typography>
                <OutlinedInput
                  onFocus={onFocusInput}
                  onChange={({ target: { value } }) =>
                    handleChangeSearchCondition({ invoiceNumber: value })
                  }
                  value={searchConditionInput.invoiceNumber}
                  sx={{ width: 250 }}
                />
              </Stack>
              <Stack
                direction="row"
                flexWrap="wrap"
                alignItems="center"
                justifyContent={{ sm: "space-between", md: "left" }}
                width={{ md: "auto", sm: 380 }}
              >
                <Typography>最終支払日:&nbsp;</Typography>
                <Stack
                  direction={{ sm: "column", md: "row" }}
                  flexWrap="wrap"
                  alignItems="center"
                  justifyContent={{ sm: "space-between", md: "left" }}
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    marginBottom={{ md: 0, xs: 2 }}
                  >
                    <DateInput
                      value={searchConditionInput.fromDate}
                      setValue={(value) =>
                        handleChangeSearchCondition({ fromDate: value })
                      }
                    />
                    <Typography> &nbsp;から&nbsp;</Typography>
                  </Stack>
                  <Stack direction="row" alignItems="center">
                    <DateInput
                      value={searchConditionInput.endDate}
                      setValue={(value) =>
                        handleChangeSearchCondition({ endDate: value })
                      }
                    />
                    <Typography> &nbsp;まで&nbsp;</Typography>
                  </Stack>
                </Stack>
              </Stack>
            </Stack>
            <Stack
              direction="row"
              alignItems="center"
              flexWrap="wrap"
              marginTop={2}
            >
              <Stack direction="row" alignItems="center">
                <Checkbox
                  checked={searchConditionInput.partition}
                  onChange={(_e, v) =>
                    handleChangeSearchCondition({ partition: v })
                  }
                />
                <Typography>仕切ありで絞る</Typography>
              </Stack>
              <Stack direction="row" alignItems="center">
                <Checkbox
                  checked={searchConditionInput.payment}
                  onChange={(_e, v) =>
                    handleChangeSearchCondition({ payment: v })
                  }
                />
                <Typography>支払いありで絞る</Typography>
              </Stack>
              <Stack direction="row" alignItems="center">
                <Checkbox
                  checked={searchConditionInput.balanceToday}
                  onChange={(_e, v) => {
                    const condition = v ? { negativeBalanceToday: false } : {};
                    handleChangeSearchCondition({
                      balanceToday: v,
                      ...condition,
                    });
                  }}
                />
                <Typography>残高プラスで絞る</Typography>
              </Stack>
              <Stack direction="row" alignItems="center">
                <Checkbox
                  checked={searchConditionInput.negativeBalanceToday}
                  onChange={(_e, v) => {
                    const condition = v ? { balanceToday: false } : {};
                    handleChangeSearchCondition({
                      negativeBalanceToday: v,
                      ...condition,
                    });
                  }}
                />
                <Typography>残高マイナスで絞る</Typography>
              </Stack>
            </Stack>
          </Grid>

          <Grid item xs="auto">
            <Button variant="contained" onClick={onClickSearch}>
              検索
            </Button>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default function AccountPayableConfirmation() {
  // どのメニューから遷移したかに応じて表示切替用のモードを設定する
  const location = useLocation();
  const pathMode = location?.pathname.split("/")[2];
  const mode = (pathMode === "account_payable_confirmation") ? DATE_MODE.DATE : DATE_MODE.MONTH;

  // 検索条件管理項目定義
  const [shippingDateInput, setShippingDateInput] = useState(getHyphenedDate(new Date()));
  const [tableTabPosition, setTableTabPosition] = useState(1);
  const [searchConditionInput, setSearchConditionInput] = useState({
    keyWord: "",
    invoiceNumber: "",
    fromDate: "",
    endDate: "",
    partition: false,
    payment: false,
    balanceToday: false,
    negativeBalanceToday: false,
  });

  // 画面を日計表と月次残高一覧で切り替えた際の初期化処理
  useEffect(() => {
    setShippingDateInput((mode === DATE_MODE.DATE) ? getHyphenedDate(new Date()) : getHyphenedDate(new Date()).substring(DATE_STR_START, MONTH_STR_END));
    setTableTabPosition(1);
    setSearchConditionInput({
      keyWord: "",
      invoiceNumber: "",
      fromDate: "",
      endDate: "",
      partition: false,
      payment: false,
      balanceToday: false,
      negativeBalanceToday: false,
    });
  }, [mode]);

  const [state, setState] = useState({
    pagination: { page: 0, pageSize: 25, totalPage: 1 },
  });

  const [totalPaymentData, setTotalPaymentData] = useState([]);
  const [accountPaymentData, setAccountPaymentData] = useState([]);

  const { pagination } = state;

  const updateShippingDate = (shippingDate) => {
    setShippingDateInput(shippingDate);
  };

  const onSearchCondition = () => {
    getData({ page: 0 });
  };

  const handleChangePageProps = (page) => {
    setState({ ...state, pagination: { ...pagination, page } });
  };

  const initPagination = () => {
    setState({
      ...state,
      pagination: { page: 0, pageSize: 25, totalPage: 1 },
    });
  };

  // 買掛金情報検索処理
  const getData = async (params = {}) => {
    const date = (mode === DATE_MODE.DATE ? shippingDateInput : shippingDateInput.substring(DATE_STR_START, MONTH_STR_END) + '-01');
    const param = {
      mode: mode.ID,
      date: date,
      division: tableTabPosition,
      ...searchConditionInput,
      page: pagination.page,
      size: pagination.pageSize,
      ...params,
    };
    const result = await getPayableDailyConfirmation(param);
    if (result.data) {
      const {
        data: { items, page, pageSize, totalPage },
        total,
      } = result.data;
      // const totals = ;
      setTotalPaymentData(
        total.map((t) => {
          t.index = total.indexOf(t) + 1;
          return t;
        })
      );
      setAccountPaymentData(
        items.map((i) => {
          i.index = items.indexOf(i);
          return i;
        })
      );
      setState({
        ...state,
        pagination: { ...pagination, pageSize, page, totalPage },
      });
    }
  };

  const changeSearchConditionInput = (e) => {
    setSearchConditionInput({
      ...searchConditionInput,
      ...e,
    });
  };

  // ページ/タブ/対象日および対象年月切替時の再検索処理
  useEffect(() => {
    getData();
  }, [pagination.page, tableTabPosition, shippingDateInput]);

  return (
    <CompanyTitledPage title={mode === DATE_MODE.DATE ? "買掛金日計表確認" : "買掛金残高一覧"} hideCurrentTime>
      <Grid container spacing={2} alignItems="center" marginBottom={2}>
        <Grid item xs>
          <Typography sx={{ fontSize: 20, textAlign: "right" }}>
            {mode === DATE_MODE.DATE ? "日付 :" : "年月："}
          </Typography>
        </Grid>
        <Grid item xs="auto">
          <DateInput
            value={mode === DATE_MODE.DATE ? shippingDateInput : shippingDateInput.substring(DATE_STR_START, MONTH_STR_END)}
            setValue={updateShippingDate}
            maxDate={getHyphenedDate(new Date())}
            mode={mode}
          />
        </Grid>
      </Grid>
      <AccountSearchArea
        onSearchCondition={onSearchCondition}
        searchConditionInput={searchConditionInput}
        changeSearchConditionInput={changeSearchConditionInput}
      />
      <NormalTable columns={totalPaymentColumn} data={totalPaymentData} />
      <Grid item xs>
        <Tabs
          value={tableTabPosition}
          onChange={(_e, tabPos) => {setTableTabPosition(tabPos); initPagination();}}
        >
          <Tab label="地物" value={1} />
          <Tab label="旅物" value={2} />
        </Tabs>
      </Grid>
      <NormalTable
        columns={mode === DATE_MODE.DATE ? accountPaymentColumn : accountPaymentMonthlyColumn}
        data={accountPaymentData}
        pagination={{ ...pagination, handleChangePageProps }}
        paginationTop
      />
    </CompanyTitledPage>
  );
}
